import React, { useContext } from 'react';
import { Lock } from 'lucide-react';
import type { OrgChartPerson } from '../../../types/purchasing';
import { isOrgChartBot, ORG_SEAT_LABELS, orgSeatOf, resolveOrgPlacement } from '../../../utils/orgChart';
import UserAvatar from '../../ui/UserAvatar';
import { OrgChartEditContext } from './OrgChartEditContext';

type Props = {
  person: OrgChartPerson;
  selected: boolean;
  dragging: boolean;
  dropTarget: boolean;
  compact?: boolean;
  onSelect: () => void;
};

const OrgPersonNode: React.FC<Props> = ({ person, selected, dragging, dropTarget, compact = false, onSelect }) => {
  const { canEdit, outOfSyncUids } = useContext(OrgChartEditContext);
  const seat = orgSeatOf(person);
  const isBot = isOrgChartBot(person);
  const pinned = person.pinned === true || seat !== 'staff' || isBot;
  const seatLabel = isBot ? 'Bot' : ORG_SEAT_LABELS[seat];
  const group = seat === 'contractor' ? 'pool' : resolveOrgPlacement(person).orgDepartment;
  const outOfSync = outOfSyncUids.has(person.uid);
  const movable = canEdit && !pinned;

  return (
    <article
      className={[
        'people-org-person',
        `is-group-${group}`,
        movable ? 'is-editable' : '',
        pinned ? 'is-pinned' : '',
        compact ? 'is-compact' : '',
        selected ? 'is-selected' : '',
        dragging ? 'is-dragging' : '',
        dropTarget ? 'is-drop-target' : '',
      ].filter(Boolean).join(' ')}
      onClick={e => {
        e.stopPropagation();
        onSelect();
      }}
    >
      {(seatLabel || pinned) && (
        <div className="people-org-seat-row">
          {seatLabel && (
            <span className={`people-org-seat ${isBot ? 'is-seat-bot' : `is-seat-${seat}`}`}>
              {seatLabel}
            </span>
          )}
          {pinned && <Lock size={10} aria-label="Pinned" className="people-org-lock" />}
        </div>
      )}
      <UserAvatar
        className="people-org-avatar"
        name={person.displayName}
        photoURL={person.photoURL}
        userId={person.uid}
      />
      <div className="people-org-name">{person.displayName}</div>
      <div className="people-org-meta">{person.title || person.role}</div>
      {outOfSync && canEdit && (
        <span
          className="people-org-sync-badge"
          title="Stored manager differs from the reporting rules. Apply rules to update."
        >
          Sync
        </span>
      )}
    </article>
  );
};

export default OrgPersonNode;
